import { FC, useEffect, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { RoomInfoDto } from 'src/api/roomService';
import { Button } from 'src/components/button';
import { PaymentPlanView } from 'src/features/payment-plan/payment-plan-view';
import { useCreateStl } from 'src/lib/hooks/use-post-create-stl';
import { useHandleQueryError } from 'src/lib/hooks/use-handle-query-error';
import { InstalmentType } from 'src/shared/enum/InstalmentType';

import { styles } from './styles';
import { InstalmentSelector } from './instalment-selector';
import { StudentCounter } from './student-counter';

interface CostBreakdownProps {
    roomCode: string;
    academicYear: string;
    moveInDate: string;
    duration: number;
    roomsUrl: string;
    roomInfo?: RoomInfoDto;
    loading: boolean;
    hasError: boolean;
}

export const CostBreakdown: FC<CostBreakdownProps> = ({
    roomCode,
    academicYear,
    moveInDate,
    duration,
    roomsUrl,
    roomInfo,
    loading,
    hasError,
}) => {
    const { t } = useTranslation();
    const handleQueryError = useHandleQueryError();
    const {
        mutate: createStl,
        isPending: isCreatingStl,
        isError: hasStlError,
        data: createdStl,
        reset: resetCreateStl,
    } = useCreateStl();

    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) resetCreateStl();
        };

        window.addEventListener('pageshow', handlePageShow);

        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [resetCreateStl]);

    const isRedirectingToBooking = Boolean(createdStl?.booking_url);
    const hasBookingError = hasStlError || Boolean(createdStl && !createdStl.booking_url);
    const [selectedStudents, setSelectedStudents] = useState<number | null>(null);
    const [selectedInstalmentType, setSelectedInstalmentType] = useState<string | null>(null);

    if (hasError) {
        return (
            <Box css={styles.breakdown}>
                <Typography variant="body3" role="alert">
                    {t('something_went_wrong')}
                </Typography>
            </Box>
        );
    }

    const minStudents = roomInfo?.minNumOfStudents ?? 1;
    const maxStudents = roomInfo?.maxNumOfStudents ?? minStudents;
    const numberOfStudents = Math.min(
        Math.max(selectedStudents ?? minStudents, minStudents),
        maxStudents
    );
    const paymentPlans = roomInfo?.paymentOptions[numberOfStudents] ?? {};
    const instalmentTypes = Object.keys(paymentPlans);
    const instalmentType =
        selectedInstalmentType && instalmentTypes.includes(selectedInstalmentType)
            ? selectedInstalmentType
            : instalmentTypes[0];
    const paymentPlan = instalmentType ? paymentPlans[instalmentType] : undefined;

    const handleBookNow = () =>
        createStl(
            {
                roomCode,
                moveInDate,
                duration,
                students: numberOfStudents,
                ...(instalmentType === InstalmentType.INSTALMENTS_2 && { instalments: 2 }),
            },
            {
                onSuccess: ({ booking_url: bookingUrl }) => {
                    // booking_url is a full URL on the booking domain, so leave the SPA for it
                    if (bookingUrl) window.location.assign(bookingUrl);
                },
                onError: (error) => handleQueryError(error, academicYear, false, roomsUrl),
            }
        );

    // Skeleton the whole panel while loading, rather than PaymentPlanView's own skeleton which shows its headings
    if (loading || !roomInfo) {
        return (
            <Box css={styles.breakdown} aria-busy>
                <Skeleton variant="rectangular" css={styles.studentCounterSkeleton} />
                <Skeleton variant="rectangular" css={styles.paymentPlanSkeleton} />
                <Skeleton variant="rectangular" css={styles.ctaSkeleton} />
            </Box>
        );
    }

    return (
        <Box css={styles.breakdown}>
            <StudentCounter
                value={numberOfStudents}
                min={roomInfo.minNumOfStudents}
                max={roomInfo.maxNumOfStudents}
                onChange={setSelectedStudents}
            />

            {instalmentType && instalmentTypes.length > 1 && (
                <InstalmentSelector
                    paymentPlans={paymentPlans}
                    value={instalmentType}
                    currency={roomInfo.currency}
                    onChange={setSelectedInstalmentType}
                />
            )}

            <PaymentPlanView
                currency={roomInfo.currency}
                loading={false}
                createdPaymentPlan={paymentPlan}
            />

            <Button
                variant="contained"
                css={styles.cta}
                onClick={handleBookNow}
                disabled={!paymentPlan}
                loading={isCreatingStl || isRedirectingToBooking}
            >
                {t('view_room_book_now')}
            </Button>

            {hasBookingError && (
                <Typography variant="body3" role="alert">
                    {t('something_went_wrong')}
                </Typography>
            )}
        </Box>
    );
};
